/**
 * hat-location-image-gallery
 *
 * Attributes:
 *   mode: "view" | "edit"  (default: "view")
 *   primary-image-id: string
 *
 * Public API (edit mode):
 *   gallery.setImages(images)        — load existing server images
 *   gallery.addPhoto(blob)           — append a new local blob
 *   gallery.primaryImageId           — current primary id (server or tempId)
 *   gallery.deletedImageIds          — Set of server image ids to delete
 *   gallery.newPhotos                — [{ blob, tempId }]
 *
 * Events emitted:
 *   'primary-changed'   detail: { id }               (edit mode)
 *   'image-deleted'     detail: { id }                (edit mode)
 *   'image-expanded'    detail: { documentId?, url? } (both modes)
 */
import { HatElement } from '@/components/hat-element.js';

class HatLocationGallery extends HatElement {
  private _images: any[] = [];
  private _newPhotos: any[] = [];
  private _deletedImageIds = new Set<string>();
  private _primaryImageId: string | null = null;

  // ─── Observed attributes ──────────────────────────────────────────────────

  static get observedAttributes() {
    return ['mode', 'primary-image-id'];
  }

  attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null) {
    if (oldVal === newVal) return;
    if (name === 'primary-image-id') {
      this._primaryImageId = newVal || null;
      this._renderPreviews();
    }
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();
    this._renderShell();
    this._renderPreviews();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Revoke all blob URLs to avoid memory leaks
    this._newPhotos.forEach(p => URL.revokeObjectURL(p.url));
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  get mode() {
    return this.getAttribute('mode') || 'view';
  }

  get primaryImageId() {
    return this._primaryImageId;
  }

  get deletedImageIds() {
    return this._deletedImageIds;
  }

  get newPhotos() {
    return this._newPhotos;
  }

  setImages(images = []) {
    this._images = images;
    this._renderPreviews();
  }

  addPhoto(blob: Blob) {
    const photo = {
      blob,
      url: URL.createObjectURL(blob),
      tempId: crypto.randomUUID()
    };

    this._newPhotos.push(photo);

    // Auto-select as primary if nothing is set yet
    if (!this._primaryImageId) {
      this._primaryImageId = photo.tempId;
    }

    this._renderPreviews();
  }

  // ─── Rendering ────────────────────────────────────────────────────────────

  _renderShell() {
    this.root.innerHTML = `
      <style>
        :host {
          display: block;
        }

        .gallery {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        /* view mode: horizontal scroll strip */
        :host([mode="view"]) .gallery {
          flex-wrap: nowrap;
          overflow-x: auto;
          padding-bottom: 8px;
        }

        .thumb {
          position: relative;
          width: 90px;
          height: 90px;
          border-radius: 10px;
          overflow: hidden;
          flex-shrink: 0;
          cursor: pointer;
        }

        /* primary selection highlight (edit mode) */
        .thumb.selected {
          outline: 3px solid #4caf50;
          outline-offset: 2px;
        }

        .thumb hat-image,
        .thumb img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          border-radius: 10px;
        }

        .thumb button {
          position: absolute;
          top: 4px;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: none;
          color: white;
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          padding: 0;
          line-height: 1;
        }

        .expand-btn {
          right: 28px;
          background: rgba(40,40,40,0.9);
        }

        :host([mode="view"]) .expand-btn {
          right: 4px;
        }

        .expand-btn:hover {
          background: rgba(20,20,20,1);
        }

        .delete-btn {
          right: 4px;
          background: rgba(180,0,0,0.9);
        }

        .delete-btn:hover {
          background: rgba(140,0,0,1);
        }
      </style>

      <div class="gallery"></div>
    `;
  }

  _renderPreviews() {
    const gallery = this.root.querySelector('.gallery');
    if (!gallery) return;

    gallery.innerHTML = '';

    // ── Existing server images ──
    this._images
      .filter(img => !this._deletedImageIds.has(img.id))
      .forEach(img => {
        const isSelected = this._primaryImageId === img.id;
        const wrap = this._makeThumb({ selected: isSelected });

        const image = document.createElement('hat-image');
        image.setAttribute('type', 'thumbnail');
        image.setAttribute('document-id', img.thumbnailDocId);

        if (this.mode === 'edit') {
          image.onclick = () => this._selectPrimary(img.id);
        }

        wrap.appendChild(image);
        wrap.appendChild(this._makeExpandBtn(() => {
          this._emit('image-expanded', { documentId: img.originalDocId });
        }));

        if (this.mode === 'edit') {
          wrap.appendChild(this._makeDeleteBtn(() => {
            this._deletedImageIds.add(img.id);
            if (this._primaryImageId === img.id) this._primaryImageId = null;
            this._emit('image-deleted', { id: img.id });
            this._renderPreviews();
          }));
        }

        gallery.appendChild(wrap);
      });

    // ── New local blobs ──
    this._newPhotos.forEach((photo, index) => {
      const isSelected = this._primaryImageId === photo.tempId;
      const wrap = this._makeThumb({ selected: isSelected });

      const img = document.createElement('img');
      img.src = photo.url;

      if (this.mode === 'edit') {
        img.onclick = () => this._selectPrimary(photo.tempId);
      }

      wrap.appendChild(img);
      wrap.appendChild(this._makeExpandBtn(() => {
        this._emit('image-expanded', { url: photo.url });
      }));

      if (this.mode === 'edit') {
        wrap.appendChild(this._makeDeleteBtn(() => {
          URL.revokeObjectURL(photo.url);
          const wasPrimary = this._primaryImageId === photo.tempId;
          this._newPhotos.splice(index, 1);
          if (wasPrimary) {
            this._primaryImageId = this._newPhotos[0]?.tempId ?? null;
          }
          this._renderPreviews();
        }));
      }

      gallery.appendChild(wrap);
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  _makeThumb({ selected = false } = {}) {
    const wrap = document.createElement('div');
    wrap.className = 'thumb' + (selected ? ' selected' : '');
    return wrap;
  }

  _makeExpandBtn(onClick: () => void) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'expand-btn';
    btn.innerHTML = '⤢';
    btn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    };
    return btn;
  }

  _makeDeleteBtn(onClick: () => void) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'delete-btn';
    btn.innerHTML = '×';
    btn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    };
    return btn;
  }

  _selectPrimary(id: string) {
    this._primaryImageId = id;
    this._emit('primary-changed', { id });
    this._renderPreviews();
  }

  _emit(eventName: string, detail: any) {
    this.dispatchEvent(new CustomEvent(eventName, {
      detail,
      bubbles: true,
      composed: true
    }));
  }
}

customElements.define('hat-location-gallery', HatLocationGallery);